import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { CustomContainer, connect } from "../../atoms/DependencyHelper";
import FormHeading from "../../atoms/checkoutForms/formHeading/FormHeading";
import DeliveryDetails from "../../molecules/reviewOrder/deliveryDetails/DeliveryDetails";
import TermsCheckbox from "../../atoms/checkoutForms/termsCheckbox/TermsCheckbox";
import ReviewItems from "./ReviewItems";
import styles from "./CheckoutReview.module.scss";
import {
  formatNumbersWithComma,
  isArrayNotEmpty,
  getCookieValue,
} from "../../../utils/helperServices";
import labels from "../../../config/labels";
import ajaxService from "../../../utils/ajaxServices";
import * as Analytics from "../../../services/analyticsUtag";
import config from "../../../config/index";
import { journeyType } from "../../../context/checkout-journey-context";

import {
  getBreakoutUrl
} from '../../../utils/miniAppUtil';

const CheckoutReview = forwardRef(
  (
    {
      cartProducts,
      addressTypes,
      statesTypes,
      countryCode,
      countryName,
      continueDisable,
      cartId,
      navigate,
      totalPrice,
      shippingAddress,
      userLoggedIn,
      profileData,
      customerStatus,
      applicationType,
      currentJourneyType,
      deliveryDetails,
      authDetailsData,
      upgradeResponse,
      cartDetails,
      creditVettingDetails,
    },
    ref
  ) => {
    const addressTypesOptions = addressTypes ? addressTypes : [];
    const stateTypesOptions = statesTypes ? statesTypes : [];
    const [helpTextData, setHelpText] = useState([]);
    const finalLabelTerms = helpTextData[0]?.helpText;

    const [viewAnalytics, setViewAnalytics] = useState(false);
    const [productsFilter, setProductsFilter] = useState(false);

    const [normalProducts, setNormalProducts] = useState([]);
    const [dealProducts, setDealProducts] = useState([]);
    const [dealPrice, setDealPrice] = useState(`R${formatNumbersWithComma(0)}`);
    const [showOnceOff, setShowOnceOff] = useState(true);

    const analyticsData = Analytics.getUtagData();

    const dealProduct = cartProducts.find(
      (cartProduct) => cartProduct?.product?.is_deal_product === 1
    );

    const dealSku = dealProduct?.product?.sku;

    useImperativeHandle(ref, () => ({
      submit: () => {
        Analytics.link({
          link_type: "o",
          link_name: `checkout: review order: ${customerStatus}: continue`,
        });
        continueDisable(true);
      },
      back: () => {
        if (applicationType == "normal") {
          Analytics.link({
            link_type: "o",
            link_name: `checkout: review order: ${customerStatus}: back to delivery`,
          });
        } else {
          Analytics.link({
            link_type: "o",
            link_name: `checkout: review order: ${customerStatus}: back to credit check`,
          });
        }
      },
    }));

    useEffect(() => {
      async function getHelpTextData() {
        const isMiniApp = getCookieValue("miniApp") === "true";
        var helpTextResults = await ajaxService.httpGet(
          "drupalBaseUrl",
          "_format=json",
          "api",
          config?.envConfigs[config?.env]?.helpText
        );

        if (isMiniApp) {

          const consentHtml = document.createElement("div");

          consentHtml.innerHTML = helpTextResults[0]?.helpText || "<p></p>";

          const links = consentHtml.getElementsByTagName("a");

          for (const link of links) {

            link.href = getBreakoutUrl(link.href);
            link.target = "_self";
          }

          helpTextResults[0].helpText = consentHtml.innerHTML.toString();
        }

        setHelpText(helpTextResults);
        if (helpTextResults && helpTextResults.status === "error") {
          Analytics.link({
            ...Analytics.getUtagData(["page_name"]),
            page_form_event: "error",
            page_errors: `${helpTextResults.reason ? helpTextResults.reason : ""
              }`,
            page_errors_code: `chk-d-g-ht`,
          });
        }
      }
      getHelpTextData();
    }, []);

    useEffect(() => {
      if (cartProducts && cartProducts.length) {
        let normalArr = [];
        let dealArr = [];
        cartProducts.forEach((cartProduct) => {
          if (cartProduct?.product?.is_deal_product === 1) {
            dealArr.push(cartProduct);
          } else {
            if (dealSku && cartProduct?.parent_product_sku == dealSku) {
              dealArr.push(cartProduct);
            } else {
              normalArr.push(cartProduct);
            }
          }
        });

        setNormalProducts(normalArr);
        setDealProducts(dealArr);
      } else {
        setNormalProducts([]);
        setDealProducts([]);
      }
      setProductsFilter(true);

      if (
        cartDetails?.isMulti &&
        creditVettingDetails &&
        (creditVettingDetails?.resultCode ===
          labels.creditVetting.lbl_resultcode_pended ||
          creditVettingDetails?.resultCode ===
          labels.creditVetting.lbl_resultcode_pending ||
          creditVettingDetails?.resultCode ===
          labels.creditVetting.lbl_resultcode_referred ||
          creditVettingDetails?.resultCode ===
          labels.creditVetting.lbl_resultcode_offline ||
          creditVettingDetails?.resultCode
            ?.toUpperCase()
            .includes(labels.creditVetting.lbl_unprocessed))
      ) {
        setShowOnceOff(false);
      } else {
        setShowOnceOff(true);
      }
    }, [cartProducts]);

    useEffect(() => {
      if (dealProducts) {
        if (dealProducts.length) {
          let price = dealProducts.reduce((accu, ele) => {
            if (dealSku && ele?.parent_product_sku == dealSku) {
              accu += ele?.monthly_recurring;
              return accu;
            } else if (ele?.product?.is_deal_product === 1) {
              accu += ele?.product?.monthly_recurring;
              return accu;
            }
          }, 0);
          price = dealProducts
            ? `R${formatNumbersWithComma(price)} × ${dealProducts[0]?.product?.payment_terms_label
            }`
            : `R${formatNumbersWithComma(0)}`;
          setDealPrice(price);
        } else {
          setDealPrice(`R${formatNumbersWithComma(0)}`);
        }
      }
    }, [dealProducts]);

    useEffect(() => {
      let buttonStatus = checkContinueButtonErrorStatus();
      if (buttonStatus) {
        continueDisable(true);
      } else {
        checkFormError("termsCheckbox");
      }
    }, [dealProducts, normalProducts]);

    function getViewAnalytics() {
      if (applicationType == "normal") {
        return {
          page_event: "view",
          page_name: `checkout: review order: ${customerStatus}`,
        };
      } else {
        if (currentJourneyType == journeyType.upgrade) {
          return {
            page_event: "view",
            page_name: `checkout: review order: ${customerStatus}`,
            delivery_type:
              deliveryDetails?.receiveType == "store" ? "collect" : "deliver",
            authentication_id_type:
              authDetailsData?.idType == "PASSPORT" ? "passport" : "id",
            upgrade_eligibility_status: upgradeResponse?.eligibilityStatus,
            upgrade_eligible_date: upgradeResponse?.upgradeDate || "",
            upgrade_customer_type: upgradeResponse?.c3dCustomer
              ? "C3D"
              : upgradeResponse?.business
                ? "CBU"
                : "OTHER",
          };
        } else {
          return {
            page_event: "view",
            page_name: `checkout: review order: ${customerStatus}`,
            delivery_type:
              deliveryDetails?.receiveType == "store" ? "collect" : "deliver",
            authentication_id_type:
              authDetailsData?.idType == "PASSPORT" ? "passport" : "id",
          };
        }
      }
    }

    useEffect(() => {
      if (!viewAnalytics && customerStatus && productsFilter) {
        const analytics = getViewAnalytics();
        analytics.multicart_flag = cartDetails?.isMulti ? true : false;

        Analytics.view({
          ...analytics,
          sales_product_category: getCategoriesArr(
            applicationType == "normal" ? normalProducts : dealProducts
          ),
          sales_product_id: getProductNameArr(
            applicationType == "normal" ? normalProducts : dealProducts
          ),
          sales_product_name: getProductSkuArr(
            applicationType == "normal" ? normalProducts : dealProducts
          ),
          sales_product_price: getPriceArr(
            applicationType == "normal" ? normalProducts : dealProducts
          ),
          sales_product_quantity: getQuantityArr(
            applicationType == "normal" ? normalProducts : dealProducts
          ),
          ...Analytics.getProfileData(userLoggedIn, profileData, authDetailsData),
        });

        setViewAnalytics(true);
      }
    }, [customerStatus, productsFilter]);

    const [state, setState] = useState({
      validations: {
        termsCheckbox: {
          terms: true,
        },
      },
      fieldErrors: {
        termsCheckbox: {
          terms: false,
        },
        fail: false,
      },
      fieldValues: {
        termsCheckbox: {
          terms: false,
        },
      },
      fieldErrorsMessage: {
        termsCheckbox: {
          terms: "Please accept the terms & conditions before continuing",
        },
      },
    });

    const { fieldErrors, fieldValues, fieldErrorsMessage } = state;

    const getPriceAndLabel = (dealProducts, normalProducts) => {
      let price, priceLabel;

      if (isArrayNotEmpty(normalProducts)) {
        price = `R${formatNumbersWithComma(totalPrice)}`;
        priceLabel = labels.lbl_onceoff_subtotal;
      }

      if (isArrayNotEmpty(dealProducts)) {
        price = dealPrice;
        priceLabel = labels.lbl_monthly_subtotal;
      }

      return { price, priceLabel };
    };

    const getTitle = (dealProducts, normalProducts) => {
      if (isArrayNotEmpty(normalProducts) && !cartDetails?.isMulti) {
        return "You’re about to purchase:";
      }

      if (
        isArrayNotEmpty(normalProducts) &&
        cartDetails?.isMulti &&
        showOnceOff
      ) {
        return "Your once-off purchase items:";
      }

      if (
        isArrayNotEmpty(dealProducts) &&
        dealProducts[0]?.customizable_options[0]?.values[0]?.label ===
        labels.deal_details.lbl_new_contract_deal_option
      ) {
        return "Your contract:";
      }

      if (
        isArrayNotEmpty(dealProducts) &&
        dealProducts[0]?.customizable_options[0]?.values[0]?.label ===
        labels.deal_details.lbl_upgrade_deal_option
      ) {
        return "Your upgrade contract:";
      }
    };

    const getItemLabel = (dealProducts, normalProducts) => {
      if (
        isArrayNotEmpty(dealProducts) &&
        dealProducts[0]?.customizable_options[0]?.values[0]?.label ===
        labels.deal_details.lbl_upgrade_deal_option
      ) {
        return "Upgrade";
      }

      if (
        isArrayNotEmpty(dealProducts) &&
        dealProducts[0]?.customizable_options[0]?.values[0]?.label ===
        labels.deal_details.lbl_new_contract_deal_option
      ) {
        return "New contract";
      }

      if (isArrayNotEmpty(normalProducts) && showOnceOff) {
        const totalOnceOffItems = normalProducts.reduce(
          (acc, curr) => acc + curr?.quantity,
          0
        );
        return `${totalOnceOffItems} items`;
      }
    };

    const handleChange = async (event, val, form) => {
      event.persist();
      const { name } = event.target;

      switch (name) {
        case "terms":
          if (state.validations[form].terms) {
            fieldErrors[form].terms = !event.target.checked;
            fieldErrors.fail = fieldErrors[form].terms;
          }
          break;

        default:
          break;
      }

      fieldValues[form][name] = event.target.checked;

      setState({
        ...state,
        fieldErrors,
        fieldValues,
      });

      let buttonStatus = checkContinueButtonErrorStatus();

      if (state.fieldErrors.fail || buttonStatus) {
        continueDisable(true);
      } else {
        checkFormError(form);
      }

      let linkAnalytics = {
        page_name: `checkout: review order: ${customerStatus}`,
        ...Analytics.getUtagData([
          "visitor_login_status",
          "visitor_msisdn_hash",
          "visitor_customer_id",
          "visitor_id_hash",
          "sales_product_category",
          "sales_product_id",
          "sales_product_name",
          "sales_product_price",
          "sales_product_quantity",
        ]),
        visitor_subscription_type:
          applicationType == "normal"
            ? analyticsData.visitor_subscription_type
            : "postpaid",
      };
      if (event.target.checked) {
        Analytics.link({
          ...linkAnalytics,
          link_type: "o",
          page_tool_event: "use",
          page_tool_name: `checkout: review order: ${customerStatus}: accept terms and conditions`,
        });
      } else {
        Analytics.link({
          ...linkAnalytics,
          page_form_event: "error",
          page_errors: state.fieldErrorsMessage.termsCheckbox.terms,
          page_form_name: `checkout: review order: ${customerStatus}: terms`,
          page_error_type: "terms and conditions",
        });
      }
    };

    function checkContinueButtonErrorStatus() {
      const { isMulti, contract, onceOff } = cartDetails;
      if (isMulti || contract || onceOff) {
        return false;
      } else {
        return true;
      }
    }

    function checkFormError(form) {
      var values = state.fieldValues[form];
      var entries = Object.entries(state.validations[form]);

      for (const [key, value] of entries) {
        if (!values[key] && value) {
          continueDisable(true);
          return true;
        }
      }

      continueDisable(false);
    }

    const getCategoriesArr = (products) => {
      if (products.length > 0) {
        if (applicationType == "normal") {
          return products.map((item) => item?.product?.categories?.[0]?.name);
        } else {
          return products.map((item) => item?.product?.categories?.[1]?.name);
        }
      }
      return [];
    };

    const getQuantityArr = (products) => {
      if (products.length > 0) {
        return products.map((item) => item?.quantity);
      }
      return [];
    };

    function getDealFormattedPrice(item) {
      return `${item?.product?.monthly_recurring} x ${item?.product?.payment_terms_label}`;
    }

    const getPriceArr = (products) => {
      if (products.length > 0) {
        if (applicationType == "normal") {
          return products.map(
            (item) => item?.prices?.row_total_original?.value
          );
        } else {
          return products.map((item) => getDealFormattedPrice(item));
        }
      }
      return [];
    };

    const getProductSkuArr = (products) => {
      if (products.length > 0) {
        return products.map((item) => item?.product?.sku);
      }
      return [];
    };

    const getProductNameArr = (products) => {
      if (products.length > 0) {
        return products.map((item) => item?.product?.name);
      }
      return [];
    };

    return (
      <>
        <CustomContainer className={styles["review-container"]}>
          <CustomContainer className={styles["agree"]}>
            <FormHeading
              title="The fine print:"
              className={styles["top-wrapper"]}
              headingClassName={styles["top-wrapper__heading"]}
            />
            <TermsCheckbox
              fieldName="terms"
              label={finalLabelTerms && finalLabelTerms}
              fieldValues={state.fieldValues["termsCheckbox"]}
              fieldErrors={state.fieldErrors["termsCheckbox"]}
              fieldErrorsMessage={state.fieldErrorsMessage["termsCheckbox"]}
              onChangeHandler={handleChange}
              onBlurHandler={() => { }}
              className={styles["section"]}
            ></TermsCheckbox>
          </CustomContainer>
          {isArrayNotEmpty(dealProducts) && (
            <ReviewItems
              isDeal={true}
              title={getTitle(dealProducts, [])}
              price={getPriceAndLabel(dealProducts, []).price}
              itemLabel={getItemLabel(dealProducts, [])}
              priceLabel={getPriceAndLabel(dealProducts, []).priceLabel}
              customerStatus={customerStatus}
              cartProducts={dealProducts}
              cartId={cartId}
              showOnceOff={showOnceOff}
            />
          )}
          {isArrayNotEmpty(normalProducts) && showOnceOff && (
            <ReviewItems
              isDeal={false}
              title={getTitle([], normalProducts)}
              price={getPriceAndLabel([], normalProducts).price}
              itemLabel={getItemLabel([], normalProducts)}
              priceLabel={getPriceAndLabel([], normalProducts).priceLabel}
              customerStatus={customerStatus}
              cartProducts={normalProducts}
              cartId={cartId}
              showOnceOff={showOnceOff}
            />
          )}
          <CustomContainer className={styles["address"]}>
            <FormHeading
              title="Delivery details:"
              className={styles["top-wrapper"]}
              headingClassName={styles["top-wrapper__heading"]}
            />
            <DeliveryDetails
              addressTypes={addressTypesOptions}
              statesTypes={stateTypesOptions}
              countryCode={countryCode}
              countryName={countryName}
              customerStatus={customerStatus}
              shippingAddress={shippingAddress}
              cartId={cartId}
              className={styles["section"]}
              userLoggedIn={userLoggedIn}
              applicationType={applicationType}
            ></DeliveryDetails>
          </CustomContainer>
        </CustomContainer>
      </>
    );
  }
);

CheckoutReview.propTypes = {
  cartProducts: PropTypes.array,
  continueDisable: PropTypes.func,
  addressTypes: PropTypes.array,
  statesTypes: PropTypes.array,
  countryCode: PropTypes.string,
  countryName: PropTypes.string,
  cartId: PropTypes.string,
  navigate: PropTypes.func,
  totalPrice: PropTypes.number,
  totalQuantity: PropTypes.number,
  userLoggedIn: PropTypes.bool,
  profileData: PropTypes.any,
  customerStatus: PropTypes.string,
  applicationType: PropTypes.string,
};

CheckoutReview.defaultProps = {
  cartProducts: [],
  addressTypes: [],
  statesTypes: [],
  totalPrice: 0,
  totalQuantity: 0,
  applicationType: "normal",
};

const mapStateToProps = (state) => {
  return {
    deliveryDetails: state.creditCheckDetails.deliveryDetails,
    authDetailsData: state.checkoutDetails.authDetails,
    upgradeResponse: state.upgrade.upgradeResponse,
    creditVettingDetails: state.checkoutDetails.creditVettingResult,
  };
};

export default connect(mapStateToProps, null, null, {
  forwardRef: true,
})(CheckoutReview);
